import Phaser from "phaser";
import { IUpdateGameScoreRequest } from "./api";

export interface Point {
  x: number;
  y: number;
}

export interface Intersection {
  x: number;
  y: number;
  openPaths: string[];
}

export interface Ghost extends Phaser.Physics.Arcade.Sprite {
  originalTexture: string;
  direction: string;
  previousDirection: string;
  nextIntersection: Point | null;
  enteredMaze: boolean;
  hasBeenEaten: boolean;
  path: Point[];
  entryTimer?: ReturnType<typeof setTimeout>;
  stuckTimer?: number;
  blinkInterval?: ReturnType<typeof setInterval>;
  // Add Phaser sprite properties that we need
  texture: Phaser.Textures.Texture;
  x: number;
  y: number;
}

// Pill interface for number pills
export interface NumberPill extends Phaser.GameObjects.Container {
  number: number;
  digitCount: number;
  x: number;
  y: number;
}

// Question data structure for pill placement
export interface QuestionData {
  answers: number[];
  correctAnswer: number;
}

// Type for scene initialization data
export interface GameSceneData {
  setIsGameAssetsLoaded?: (isGameAssetsLoaded: boolean) => void;
  setAssetLoadingProgress?: (assetLoadingProgress: number) => void;
  increaseSmallDotsCount?: (count: number) => void;
  increasePowerPillsCount?: (count: number) => void;
  increaseCorrectAnswersCount?: (count: number) => void;
  onGameOver?: () => void;
  onLifeLost?: (livesRemaining: number) => void;
  onGameStateChange?: (state: GameState) => void;
  onPillEaten?: (answer: number, correctAnswer: number) => void;
  onWrongAnswer?: () => void;
  setRunningGameState?: (runningGameState: RunningGameState) => void;
  onGamePaused?: () => void;
  // Callback to request question data from React when pills need to be placed
  requestQuestion?: () => QuestionData | null;
}

// Game state enumeration
export enum GameState {
  LOADING = "loading",
  MENU = "menu",
  START_DELAY = "start_delay",
  RESPAWN_DELAY = "respawn_delay",
  END_DELAY = "end_delay",
  PLAYING = "playing",
  PAUSED = "paused",
  GAME_OVER = "game_over",
  VICTORY = "victory",
}

// Running game state enumeration
export enum RunningGameState {
  DEFAULT = "default",
  RUNNING = "running",
  DELAY_PHASE = "delay_phase",
}

// Interface for game event communication
export interface PacmanGameEvents {
  startGame(): Promise<boolean>;
  endGame(): Promise<boolean>;
  pauseGame(): Promise<boolean>;
  resumeGame(): Promise<boolean>;
  resetGame(): Promise<boolean>;
  getCurrentState(): GameState;
  isGameActive(): boolean;
  toggleSoundMute(): boolean;
  setMasterVolume(volume: number): void;
  setSfxVolume(volume: number): void;
  setMusicVolume(volume: number): void;
  isSoundMuted(): boolean;
  getMasterVolume(): number;
  getSfxVolume(): number;
  getMusicVolume(): number;
  placeNumberPills(answers: number[], correctAnswer: number): Promise<boolean>;
  clearPillsPlacedFlag(): void;
}

// Type for game event handlers
export interface GameEventHandlers {
  onStart?: () => void;
  onEnd?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onReset?: () => void;
  onStateChange?: (newState: GameState, previousState: GameState) => void;
}

// Error types for game event communication
export interface GameEventError {
  type: "INVALID_STATE" | "GAME_NOT_READY" | "OPERATION_FAILED";
  message: string;
  currentState?: GameState;
}

export interface PacmanGame extends Phaser.Scene, PacmanGameEvents {
  direction: string;
  previousDirection: string;
  blockSize: number;
  board: number[][];
  speed: number;
  ghostSpeed: number;
  intersections: Intersection[];
  nextIntersection: Point | null;
  oldNextIntersection: Point | null;
  scatterModeDuration: number;
  chaseModeDuration: number;
  scaredModeDuration: number;
  modeTimer: ReturnType<typeof setTimeout> | null;
  currentMode: string;
  previouseMode?: string;
  lives: number;
  isPacmanAlive: boolean;
  hasRespawned: boolean;
  pacman: Phaser.Physics.Arcade.Sprite;
  map: Phaser.Tilemaps.Tilemap;
  dots: Phaser.Physics.Arcade.Group;
  powerPills: Phaser.Physics.Arcade.Group;
  pills: Phaser.Physics.Arcade.Group;
  cursors: Phaser.Types.Input.Keyboard.CursorKeys;
  pinkGhost: Ghost;
  orangeGhost: Ghost;
  redGhost: Ghost;
  blueGhost: Ghost;
  ghosts: Ghost[];
  // Game state management
  gameState: GameState;
  // Functions passed from App.tsx
  increaseScore?: (points: number) => void;
  onGameOver?: (finalScore: number) => void;
  onLifeLost?: (livesRemaining: number) => void;
  onGameStateChange?: (state: GameState) => void;
  onPillEaten?: (answer: number, correctAnswer: number) => void;
  onWrongAnswer?: () => void;
  correctAnswer?: number; // Current correct answer for validation
}

// Type for Phaser.Game instance with proper scene management
export interface PhaserGameInstance extends Phaser.Game {
  scene: Phaser.Scenes.SceneManager;
  registry: Phaser.Data.DataManager;
  destroy(removeCanvas: boolean): void;
}

export interface IGameData extends IUpdateGameScoreRequest {}
